import useCreateDocumentInstance from '@/api/mutations/documentInstance/useCreateDocumentInstance';
import UploadTemplateField from '@/ui/components/uploadTemplateField/UploadTemplateField';
import SidebarLayout from '@/ui/pages/shared/SidebarLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonRadioGroup,
  Field,
  Fieldset,
  Form,
  RawInput,
} from '@payaca/ambrosia';

import { useFirstMountState } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import { z } from 'zod/v4';

const FORM_SCHEMA = z.object({
  name: z.string().min(1, 'Name is required').max(1023),
});

type TFormSchema = z.infer<typeof FORM_SCHEMA>;

export const CreateDocumentInstanceForm: FC<{
  projectId: string;
  templateId: string;
  folderId?: string;
  templatePreviewHtml: string;
  defaultValues: TFormSchema;
  onSuccess: () => void;
}> = (props) => {
  const {
    projectId,
    templateId,
    folderId,
    defaultValues,
    templatePreviewHtml,
    onSuccess,
  } = props;

  const [isMobileView, setIsMobileView] = useState(false);

  const formMethods = useForm<TFormSchema>({
    defaultValues,
    resolver: zodResolver(FORM_SCHEMA),
  });

  const name = formMethods.watch('name');

  const { mutateAsync, error, isPending } = useCreateDocumentInstance({
    projectId,
  });

  const onSubmit: SubmitHandler<TFormSchema> = async (data) => {
    await mutateAsync({
      name: data.name,
      projectId: projectId,
      templateId: templateId,
      folderId: folderId,
    });
    onSuccess();
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit} className="h-full">
      <SidebarLayout>
        <SidebarLayout.Body>
          <div className="flex items-center justify-between mb-4">
            <h1>&quot;{name}&quot; preview</h1>

            <ButtonRadioGroup
              value={isMobileView}
              sizeVariant="sm"
              onChange={setIsMobileView}
              options={[
                {
                  label: 'Desktop',
                  value: false,
                },
                {
                  label: 'Mobile',
                  value: true,
                },
              ]}
              CustomOption={({ option: { label, value } }) => {
                return (
                  <>
                    <UntitledIcon
                      className="w-5 h-5"
                      name={value ? 'phone-01.3' : 'monitor-01.3'}
                    />
                    {label}
                  </>
                );
              }}
            />
          </div>

          <UploadTemplateField
            isMobile={isMobileView}
            name="templateHtml"
            value={{
              preview: templatePreviewHtml,
              raw: templatePreviewHtml,
            }}
            // doesn't change in this form
            onChange={() => {}}
          />
        </SidebarLayout.Body>

        <SidebarLayout.Sidebar>
          <SidebarLayout.Sidebar.Body>
            <Fieldset>
              <Field name="name">
                <Field.Label>Name</Field.Label>
                <RawInput {...formMethods.register('name')} />
              </Field>
              <Button
                type="submit"
                className="w-full"
                isProcessing={isPending}
                disabled={isPending}
              >
                Generate document
              </Button>
            </Fieldset>
          </SidebarLayout.Sidebar.Body>
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </Form>
  );
};
